import { Injectable } from '@angular/core';
import { isNullOrUndefined } from '@sla-ng/core';
import { FieldType } from '@sla-ng/ui-material';
import * as moment from 'moment';
import { Moment } from 'moment';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { ICostCenter } from '../../../models/cost-center.model';
import { ICostType } from '../../../models/cost-type.model';
import { ICostUnit } from '../../../models/cost-unit.model';
import { ICost } from '../../../models/cost.model';
import { IMovingAveragePrice } from '../../../models/moving-average-price.model';
import { IPlannedSalePrice } from '../../../models/planned-sale-price.model';
import { IPurchase } from '../../../models/purchase.model';
import { ISale } from '../../../models/sale.model';
import { ITimeRecord } from '../../../models/time-record.model';
import { IWorkingPlan } from '../../../models/working-plan.model';
import { ArticleResource } from '../../../resources/article-resource.service';
import { CostCenterResource } from '../../../resources/cost-center.resource';
import { CostTypeResource } from '../../../resources/cost-type.resource';
import { CostUnitResource } from '../../../resources/cost-unit.resource';
import { TransactionalDataResource } from '../../../resources/transactional-data.resource';
import { ITransactionalDataState } from './transactional-data.state.model';

let _state: ITransactionalDataState = {
    productionFilter: '',
    selectedProductionOrder: undefined,
    productionOrders: [],
    articleLookup: {},
    costCenterLookup: {},
    costTypeLookup: {},
    costUnitLookup: {},
    purchaseColumns: [ 'articleNr', 'article', 'date', 'priceCt', 'amount', 'measureUnit', 'priceCtPerMeasureUnit' ],
    workingPlanColumns: [ 'articleNr', 'planningSize', 'productionSize', 'constantTimeMinutes', 'linearTimeMinutes' ],
    movingAveragePriceColumns: [ 'articleNr', 'date', 'priceCents', 'measureUnit' ],
    saleColumns: [ 'articleNr' ,'costUnit', 'date', 'priceCt', 'amount', 'measureUnit', 'priceCtPerMeasureUnit' ],
    timeRecordColumns: [ 'articleNr', 'date', 'minutes' ],
    costColumns: [ 'date', 'costTypeName', 'costCenterName', 'costCents' ],
    plannedSalePriceColumns: [ 'articleNr', 'costUnit', 'priceCt', 'validFrom', 'validTo', 'measureUnit' ],
    selectedDateRange: {
        start: moment().subtract(2, 'week'),
        end: moment(),
    },
    movingAveragePrices: [],
    isLoading: false,
    purchases: [],
    sales: [],
    timeRecords: [],
    costs: [],
    plannedSalePrices: [],
    workingPlans: [],
    dateSelectField: {
        field: 'dateRange',
        type: FieldType.daterange,
        label: 'app.external-data.daterange.label',
    }
};

@Injectable({
    providedIn: 'root',
})
export class TransactionalDataFacade {

    protected store$: BehaviorSubject<ITransactionalDataState> = new BehaviorSubject<ITransactionalDataState>(_state);

    constructor(private myTransactionalDataResource: TransactionalDataResource,
                private myCostTypeResource: CostTypeResource,
                private myCostCenterResource: CostCenterResource,
                private myCostUnitResource: CostUnitResource,
                private myPurchaseArticleResource: ArticleResource) {
    }

    public getStateSnapshot(): ITransactionalDataState {
        return this.store$.value;
    }

    public subState(): Observable<ITransactionalDataState> {
        return this.store$.asObservable();
    }

    public async fetchPurchases(): Promise<void> {

        const state: ITransactionalDataState = this.getStateSnapshot();

        if (Object.keys(state.articleLookup).length === 0) {
            void this.createPurchaseArticleLookup();
        }

        this.setIsloading(true);
        const purchases: IPurchase[] = await firstValueFrom(this.myTransactionalDataResource
            .getPurchases(state.selectedDateRange.start, state.selectedDateRange.end)).catch(() => []);
        this.updateState({ ..._state, purchases });
        this.setIsloading(false);
    }

    public async fetchSales(): Promise<void> {
        const state: ITransactionalDataState = this.getStateSnapshot();

        this.setIsloading(true);

        const [ costUnitLookup, sales ]: [ Record<string, ICostUnit>, ISale[] ] = await Promise.all([
            this.createCostUnitLookup(),
            await firstValueFrom(this.myTransactionalDataResource
                .getSales(state.selectedDateRange.start, state.selectedDateRange.end)).catch(() => []),
        ]);
        this.updateState({ ..._state, costUnitLookup });
        this.updateState({ ..._state, sales });
        this.setIsloading(false);
    }

    public async fetchMovingAveragePrices(): Promise<void> {
        const state: ITransactionalDataState = this.getStateSnapshot();

        this.setIsloading(true);
        const movingAveragePrices: IMovingAveragePrice[] = await firstValueFrom(this.myTransactionalDataResource
            .getMovingAveragePrices(state.selectedDateRange.start, state.selectedDateRange.end)).catch(() => []);
        this.updateState({ ..._state, movingAveragePrices });
        this.setIsloading(false);
    }

    public async fetchTimeRecords(): Promise<void> {
        const state: ITransactionalDataState = this.getStateSnapshot();

        this.setIsloading(true);
        const timeRecords: ITimeRecord[] = await firstValueFrom(this.myTransactionalDataResource
            .getTimeRecords(state.selectedDateRange.start, state.selectedDateRange.end)).catch(() => []);
        this.updateState({ ..._state, timeRecords });
        this.setIsloading(false);
    }

    public async fetchCosts(): Promise<void> {
        const state: ITransactionalDataState = this.getStateSnapshot();

        this.setIsloading(true);
        const costs: ICost[] = await firstValueFrom(this.myTransactionalDataResource
            .getCosts(state.selectedDateRange.start, state.selectedDateRange.end)).catch(() => []);
        this.updateState({ ..._state, costs });
        this.setIsloading(false);
    }

    public async fetchCostMappingData(): Promise<void> {
        this.setIsloading(true);
        await Promise.all([ this.createCostTypeLookup(), this.createCostCenterLookup() ]);
        this.setIsloading(false);
    }

    public async fetchPlannedSalePrices(): Promise<void> {
        const state: ITransactionalDataState = this.getStateSnapshot();

        this.setIsloading(true);

        const [ costUnitLookup, plannedSalePrices ]: [ Record<string, ICostUnit>, IPlannedSalePrice[] ] = await Promise.all([
            this.createCostUnitLookup(),
            firstValueFrom(this.myTransactionalDataResource
                .getPlannedSalePrice(state.selectedDateRange.start, state.selectedDateRange.end)).catch(() => []),
        ]);
        this.updateState({ ..._state, costUnitLookup });
        this.updateState({ ..._state, plannedSalePrices });
        this.setIsloading(false);
    }

    public async fetchWorkingPlans(): Promise<void> {
        this.setIsloading(true);
        const workingPlans: IWorkingPlan[] = await firstValueFrom(this.myTransactionalDataResource.getWorkingPlans()).catch(() => []);
        this.updateState({ ..._state, workingPlans });
        this.setIsloading(false);
    }

    public async fetchProductionOrders(): Promise<void> {
        this.setIsloading(true);
        const productionOrders = await firstValueFrom(this.myTransactionalDataResource.getProductionOrders(_state.selectedDateRange.start, _state.selectedDateRange.end));
        this.updateState({ ..._state, productionOrders, isLoading: false });
    }

    public async setSelectedProductionOrder(productionOrderNr: number): Promise<void> {
        if (_state.selectedProductionOrder?.productionOrderNr === productionOrderNr) {
            return;
        }
        this.setIsloading(true);

        let selectedProductionOrder = _state.productionOrders.find(pO => pO.productionOrderNr === productionOrderNr);
        if (isNullOrUndefined(selectedProductionOrder)) {
            selectedProductionOrder = await firstValueFrom(this.myTransactionalDataResource.getProductionOrder(productionOrderNr)).catch(() => null);
        }

        if (!isNullOrUndefined(selectedProductionOrder)) {
            this.updateState({ ..._state, selectedProductionOrder });
        }
        this.setIsloading(false);
    }

    public setDateRange(daterange: { start: Moment, end: Moment }): void {
        this.updateState({ ..._state, selectedDateRange: daterange });
    }

    public clearCosts(): void {
        this.updateState({ ..._state, costs: [] });
        this.updateState({ ..._state, costTypeLookup: {}, costCenterLookup: {} });
    }


    public clearPurchases(): void {
        this.updateState({ ..._state, purchases: [] });
    }

    public clearMovingAveragePrices(): void {
        this.updateState({ ..._state, movingAveragePrices: [] });
    }

    public clearSales(): void {
        this.updateState({ ..._state, sales: [], costUnitLookup: {} });
    }

    public clearTimeRecords(): void {
        this.updateState({ ..._state, timeRecords: [] });
    }

    public clearPlannedSalePrices(): void {
        this.updateState({ ..._state, plannedSalePrices: [], costUnitLookup: {} });
    }

    public clearWorkingPlans(): void {
        this.updateState({ ..._state, workingPlans: [] });
    }

    public setProductionFilter(filter: string): void {
        this.updateState({ ..._state, productionFilter: filter });
    }

    private setIsloading(isLoading: boolean): void {
        this.updateState({ ..._state, isLoading });
    }

    private updateState(state: ITransactionalDataState): void {
        this.store$.next(_state = state);
    }

    private async createCostTypeLookup(): Promise<void> {
        this.setIsloading(true);
        const costTypeLookup: Record<string, ICostType> = {};
        const costTypes: ICostType[] = await firstValueFrom(this.myCostTypeResource.getCostTypes(false, false, false)).catch(() => []);
        for (const costType of costTypes) {
            costTypeLookup[ costType.ledgerAccountNr ] = costType;
        }
        this.updateState({ ..._state, costTypeLookup });
        this.setIsloading(false);
    }

    private async createCostCenterLookup(): Promise<void> {
        this.setIsloading(true);
        const costCenterLookup: Record<string, ICostCenter> = {};
        const costCenters: ICostCenter[] = await firstValueFrom(this.myCostCenterResource.getCostCenters()).catch(() => []);
        for (const costCenter of costCenters) {
            costCenterLookup[ costCenter.financialAccountingNr ] = costCenter;
        }
        this.updateState({ ..._state, costCenterLookup });
        this.setIsloading(false);
    }

    private async createCostUnitLookup(): Promise<Record<string, ICostUnit>> {
        const costUnits: ICostUnit[] = await firstValueFrom(this.myCostUnitResource.getAllCostUnits()).catch(() => []);
        const costUnitLookup: Record<string, ICostUnit> = {};

        for (const costUnit of costUnits) {
            costUnitLookup[ costUnit.articleNr ] = costUnit;
        }

        return costUnitLookup;
    }

    private async createPurchaseArticleLookup(): Promise<void> {
        this.setIsloading(true);
        const articleLookup: Record<number, ICostUnit> = {};
        const purchaseArticles: ICostUnit[] = await firstValueFrom(this.myPurchaseArticleResource.getArticles()).catch(() => []);
        for (const purchaseArticle of purchaseArticles) {
            articleLookup[ purchaseArticle.id ] = purchaseArticle;
        }
        this.updateState({ ..._state, articleLookup });
        this.setIsloading(false);
    }
}
